import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getAllUsers } from "@/services/api";
import { Loader2, Users, MapPin, Route, Settings2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// We will use custom icons for different routes, so we don't just rely on DefaultIcon
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// A set of colors for different route clusters
const routeColors = [
  "#2563eb", // blue
  "#dc2626", // red
  "#16a34a", // green
  "#d97706", // orange
  "#9333ea", // yellow
  "#c026d3", // purple
  "#db2777", // pink
  "#0891b2", // fuchsia
  "#4f46e5", // indigo
  "#0d9488", // cyan
];

const OUTLIER_COLOR = "#6b7280"; // gray

// Helper to calculate distance between two coordinates in meters (Haversine formula)
const getDistance = (c1, c2) => {
  if (!c1 || !c2) return Infinity;
  const R = 6371e3; // Earth radius in meters
  const φ1 = c1[0] * Math.PI / 180;
  const φ2 = c2[0] * Math.PI / 180;
  const Δφ = (c2[0] - c1[0]) * Math.PI / 180;
  const Δλ = (c2[1] - c1[1]) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

// Helper to create a colored SVG marker
const createColoredIcon = (color) => {
  const svgTemplate = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="25px" height="41px">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="white" stroke-width="1"/>
    </svg>`;
  
  return L.divIcon({
    className: "custom-colored-icon",
    html: svgTemplate,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: iconShadow,
    shadowSize: [41, 41],
    shadowAnchor: [12, 41]
  });
};

const ChangeMapView = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 16, {
        duration: 1.5
      });
    }
  }, [center, map]);
  return null;
};

// Simple K-Means clustering algorithm for geographic coordinates
const kMeans = (points, k, maxIterations = 100) => {
  if (points.length === 0 || k <= 0) return { clusters: [], assignments: [] };
  if (k >= points.length) {
    return {
      clusters: points.map(p => [...p]),
      assignments: points.map((_, i) => i)
    };
  }

  // 1. Initialize k centroids randomly from the points
  const centroids = [];
  const usedIndices = new Set();
  while (centroids.length < k) {
    const idx = Math.floor(Math.random() * points.length);
    if (!usedIndices.has(idx)) {
      usedIndices.add(idx);
      centroids.push([...points[idx]]);
    }
  }

  let assignments = new Array(points.length).fill(0);
  let changed = true;
  let iterations = 0;

  // Helper distance function (squared Euclidean is fine for simple clustering here)
  const distSq = (p1, p2) => Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2);

  while (changed && iterations < maxIterations) {
    changed = false;
    iterations++;

    // 2. Assign each point to the closest centroid
    const newAssignments = points.map(p => {
      let minD = Infinity;
      let closestIdx = 0;
      for (let i = 0; i < k; i++) {
        const d = distSq(p, centroids[i]);
        if (d < minD) {
          minD = d;
          closestIdx = i;
        }
      }
      return closestIdx;
    });

    for (let i = 0; i < points.length; i++) {
      if (assignments[i] !== newAssignments[i]) {
        changed = true;
      }
    }
    assignments = newAssignments;

    // 3. Update centroids
    const counts = new Array(k).fill(0);
    const sums = Array.from({ length: k }, () => [0, 0]);

    for (let i = 0; i < points.length; i++) {
      const clusterIdx = assignments[i];
      sums[clusterIdx][0] += points[i][0];
      sums[clusterIdx][1] += points[i][1];
      counts[clusterIdx]++;
    }

    for (let i = 0; i < k; i++) {
      if (counts[i] > 0) {
        centroids[i][0] = sums[i][0] / counts[i];
        centroids[i][1] = sums[i][1] / counts[i];
      }
    }
  }

  return { clusters: centroids, assignments };
};

const UserMap = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geocodingProgress, setGeocodingProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  
  // Kochi coordinates
  const kochiPosition = [9.9312, 76.2673];

  // Routing / Clustering state
  const [numRoutes, setNumRoutes] = useState(3);
  const [clusteredUsers, setClusteredUsers] = useState([]);
  
  // Dynamic Zone (Circle) state
  const [zoneCenter, setZoneCenter] = useState(kochiPosition);
  const [zoneRadius, setZoneRadius] = useState(15000); // 15km default

  const geocodeAddress = async (address) => {
    try {
      if (!address) return null;

      // 0. Check if address is actually coordinates
      const coordParts = address.split(',').map(p => parseFloat(p.trim()));
      if (coordParts.length === 2 && !isNaN(coordParts[0]) && !isNaN(coordParts[1])) {
        if (coordParts[0] >= -90 && coordParts[0] <= 90 && coordParts[1] >= -180 && coordParts[1] <= 180) {
          return coordParts;
        }
      }

      const fetchCoords = async (query, strategyName) => {
        try {
          const encodedQuery = encodeURIComponent(query);
          let baseUrl = window.location.hostname === 'localhost' 
  ? 'http://localhost:8001/api/admin': (process.env.REACT_APP_API_URL || 'https://api-jtgorgdsma-uc.a.run.app/api/admin');
          if (baseUrl.endsWith('/admin')) {
            baseUrl = baseUrl.replace(/\/admin$/, '');
          }
          console.log(`    [Geocode] Trying Strategy "${strategyName}" with query: "${query}"`);
          const response = await fetch(`${baseUrl}/geocode?q=${encodedQuery}`);
          
          if (!response.ok) {
            console.warn(`    [Geocode] Fetch failed (Status: ${response.status}) for query: "${query}"`);
            return null;
          }
          
          const data = await response.json();
          if (data && data.length > 0) {
            const foundCoords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
            console.log(`    [Geocode] SUCCESS! Found: [${foundCoords.join(', ')}] for query: "${query}"`);
            return foundCoords;
          }
          console.log(`    [Geocode] No results found for query: "${query}"`);
        } catch (e) {
          console.error(`    [Geocode] Error during fetch for query: "${query}"`, e);
        }
        return null;
      };

      // Pre-cleaning
      let cleanAddress = address.replace(/\n/g, ', ').replace(/\s+/g, ' ').trim();
      
      // Strategy 1: Try FULL Address string first (Google Places results are usually high quality)
      let coords = await fetchCoords(cleanAddress, "Full Address");
      if (coords) return coords;

      // Strategy 1.5: If full address failed, try appending "Kerala, India" if not present
      // Sometimes Nominatim needs explicit context
      if (!cleanAddress.toLowerCase().includes('india')) {
         coords = await fetchCoords(`${cleanAddress}, Kerala, India`, "Add Context");
         if (coords) return coords;
      }

      // If full address failed, then try Progressive Simplification
      // Google Places often returns "PlaceName, Street, Area, City, State, ZIP, Country"
      // We will try removing parts from the START one by one until we find a match (up to a limit)
      let parts = cleanAddress.split(',').map(p => p.trim()).filter(p => p.length > 0);
      
      // Try removing the first part (often a specific building name or house number that Nominatim doesn't know)
      if (parts.length > 2) {
          // Try "Street, Area, City..."
          const simplified1 = parts.slice(1).join(', ');
          coords = await fetchCoords(simplified1, "Remove Prefix 1");
          if (coords) return coords;

          // Try "Area, City..."
          const simplified2 = parts.slice(2).join(', ');
          coords = await fetchCoords(simplified2, "Remove Prefix 2");
          if (coords) return coords;
      }

      // Strategy 2: Try Last 2-3 significant parts (Locality + City + PIN)
      // This is often the most reliable "Area" search
      if (parts.length >= 2) {
        // Take the last 3 parts (usually Area, City, State/PIN)
        const lastThree = parts.slice(-3).join(', ');
        coords = await fetchCoords(lastThree, "Last 3 Parts");
        if (coords) return coords;
        
        // Take just the last 2 (City, State/PIN)
        const lastTwo = parts.slice(-2).join(', ');
        coords = await fetchCoords(lastTwo, "Last 2 Parts");
        if (coords) return coords;
      }

      // Strategy 3: PIN Code Search
      const pinCodeMatch = cleanAddress.match(/\b(6\d{5})\b/);
      let pinCode = pinCodeMatch ? pinCodeMatch[0] : null;

      if (pinCode) {
         // Try searching just the PIN code with India context
         coords = await fetchCoords(`${pinCode}, India`, "PIN Code Only");
         if (coords) return coords;
      }

      return null;
    } catch (err) {
      console.error("Geocoding error:", address, err);
      return null;
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Fetch all users with a large limit for the map view
        const data = await getAllUsers('user');
        if (data && data.success) {
          const allUsers = data.users;
          console.log(`Fetched ${allUsers.length} users for mapping.`);
          const usersWithCoords = [];
          
          // Debug arrays to store summary
          const successLog = [];
          const failureLog = [];
          
          for (let i = 0; i < allUsers.length; i++) {
            const u = allUsers[i];
          let coords = null;
          let method = "none";
          let used_location_value = "N/A";

          // 0. Try pre-verified location object first (Very fast!)
          if (u.location && typeof u.location.lat === 'number' && typeof u.location.lng === 'number') {
            coords = [u.location.lat, u.location.lng];
            method = "pre_verified_location";
          }
          // 1. Try location_string next
          else if (u.location_string && u.location_string.trim() !== "") {
            used_location_value = u.location_string;
            // Check if it's already a set of coordinates
            const parts = u.location_string.split(',').map(p => parseFloat(p.trim()));
            if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
              coords = parts;
              method = "location_string_coords";
            } else {
              // If it's not coordinates, it's an address string. Geocode IT.
              setGeocodingProgress(Math.round(((i + 1) / allUsers.length) * 100));
              // Wait much less now (Google is fast)
              await new Promise(resolve => setTimeout(resolve, 100));
              coords = await geocodeAddress(u.location_string);
              if (coords) {
                 method = "location_string_geocode";
              }
            }
          }

          // 2. Fallback to geocoding the address ONLY if location_string is ABSENT or EMPTY
          // This strictly ignores fallback if location_string exists but failed to geocode,
          // as per user requirement that 'address' field might be inaccurate.
          const hasLocationString = u.location_string && u.location_string.trim() !== "";
          
          if (!coords && !hasLocationString && u.address && u.address.trim() !== "") {
            used_location_value = u.address;
            setGeocodingProgress(Math.round(((i + 1) / allUsers.length) * 100));
            // Small delay
            await new Promise(resolve => setTimeout(resolve, 100));
            coords = await geocodeAddress(u.address);
            if (coords) {
              method = "address_geocode";
            }
          }

            if (coords) {
              // Add a tiny bit of random jitter (approx 10-20 meters) to avoid markers being exactly on top of each other
              // especially when geocoding defaults to a PIN code center.
              const jitter = () => (Math.random() - 0.5) * 0.0005;
              const jitteredCoords = [coords[0] + jitter(), coords[1] + jitter()];

              usersWithCoords.push({ ...u, coords: jitteredCoords });
              successLog.push({
                name: u.name,
                source: method,
                coords: coords,
                original_address: used_location_value
              });
            } else {
              failureLog.push({
                name: u.name,
                original_address: u.address || "N/A",
                location_string: u.location_string || "N/A",
                reason: u.address || u.location_string ? "Geocoding failed" : "No address/location provided"
              });
            }
          }
          
          setUsers(usersWithCoords);
        } else {
          setError("Failed to fetch users");
        }
      } catch (err) {
        console.error("Error fetching users for map:", err);
        setError("An error occurred while fetching user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Effect to re-run clustering when users, numRoutes, or zone changes
  useEffect(() => {
    if (users.length === 0) return;

    // Separate users within the defined Zone (Circle) and outliers
    const inZoneUsers = [];
    const outlierUsers = [];

    users.forEach(u => {
      const distance = getDistance(u.coords, zoneCenter);
      if (distance <= zoneRadius) {
        inZoneUsers.push(u);
      } else {
        // -1 indicates an outlier (not part of any route)
        outlierUsers.push({ ...u, routeIndex: -1 });
      }
    });

    if (inZoneUsers.length === 0) {
      setClusteredUsers(outlierUsers);
      return;
    }

    // Ensure numRoutes is valid based ONLY on users in zone
    const k = Math.max(1, Math.min(inZoneUsers.length, parseInt(numRoutes) || 1));
    
    // Extract coordinates for K-Means
    const points = inZoneUsers.map(u => u.coords);
    
    // Run K-Means
    const { assignments } = kMeans(points, k);
    
    // Assign route index to zone users
    const routedUsers = inZoneUsers.map((u, i) => ({
      ...u,
      routeIndex: assignments[i]
    }));

    // Combine both sets
    setClusteredUsers([...routedUsers, ...outlierUsers]);
  }, [users, numRoutes, zoneCenter, zoneRadius]);

  if (loading) {
    return (
      <div className="flex h-[600px] items-center justify-center">
        <div className="flex flex-col items-center gap-4 w-full max-w-md px-6">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <div className="space-y-2 text-center">
            <p className="text-muted-foreground font-medium text-lg">Pinpointing Customers...</p>
            <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
              <div 
                className="bg-primary h-full transition-all duration-300 ease-in-out" 
                style={{ width: `${geocodingProgress}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground italic">
              Geocoding customer addresses in Kochi ({geocodingProgress}%)
            </p>
          </div>
        </div>
      </div>
    );
  }

  const filteredSearchResults = searchQuery.trim() === "" 
    ? [] 
    : users.filter(u => 
        (u.name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (u.phone?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (u.contact?.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground text-left">Route Planning Map</h1>
          <p className="text-muted-foreground mt-1 text-left">Visualize customer density and generate collection routes</p>
        </div>
        
        <div className="relative w-full sm:w-64 z-[1000]">
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
            className="w-full"
          />
          {isSearchFocused && filteredSearchResults.length > 0 && (
            <Card className="absolute top-full left-0 w-full mt-1 shadow-xl border-border bg-background">
              <CardContent className="p-0">
                {filteredSearchResults.map((u) => (
                  <div
                    key={u.id || u._id}
                    className="px-4 py-2 hover:bg-muted cursor-pointer transition-colors border-b last:border-b-0"
                    onClick={() => {
                      setSelectedUser(u);
                      setSearchQuery(u.name);
                    }}
                  >
                    <p className="font-medium text-sm">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.phone || u.contact || "No contact"}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2 bg-muted/50 p-2 rounded-lg border border-border">
            <Settings2 className="w-4 h-4 text-muted-foreground" />
            <Label htmlFor="routes" className="text-sm font-medium">Routes:</Label>
            <Input 
              id="routes" 
              type="number" 
              min="1" 
              max="20"
              value={numRoutes} 
              onChange={(e) => setNumRoutes(e.target.value)}
              className="w-16 h-8 text-center"
            />
          </div>
          
          <div className="flex items-center gap-4 bg-muted/50 p-2 rounded-lg border border-border">
             <div className="flex items-center gap-2">
                <Label htmlFor="radius" className="text-sm font-medium whitespace-nowrap">Zone Radius:</Label>
                <input 
                  id="radius"
                  type="range"
                  min="1000"
                  max="50000"
                  step="1000"
                  value={zoneRadius}
                  onChange={(e) => setZoneRadius(parseInt(e.target.value))}
                  className="w-32 accent-primary"
                />
                <span className="text-xs font-mono w-12 text-right">{(zoneRadius/1000).toFixed(1)}km</span>
             </div>
          </div>

          <Card className="px-4 py-2 flex items-center gap-2 border-border shadow-sm">
            <Users className="w-4 h-4 text-primary" />
            <div className="text-left">
              <p className="text-[10px] text-muted-foreground uppercase font-bold leading-tight">Total Pinpointed</p>
              <p className="text-lg font-bold leading-tight">{users.length}</p>
            </div>
          </Card>
        </div>
      </div>

      <Card className="border-border overflow-hidden shadow-md">
        <CardContent className="p-0">
          <div className="h-[calc(100vh-250px)] min-h-[600px] w-full z-0 relative">
            <MapContainer
              center={kochiPosition}
              zoom={11}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Highlight Circle for Selected User */}
              {selectedUser && (
                <>
                  <ChangeMapView center={selectedUser.coords} />
                  <Circle
                    center={selectedUser.coords}
                    radius={100} // 100 meters
                    pathOptions={{ 
                      color: '#2563eb', 
                      fillColor: '#2563eb', 
                      fillOpacity: 0.2,
                      weight: 2
                    }}
                  />
                  <Circle
                    center={selectedUser.coords}
                    radius={30} // Pulsing effect inner
                    pathOptions={{ 
                      color: '#2563eb', 
                      fillColor: '#2563eb', 
                      fillOpacity: 0.5,
                      weight: 1
                    }}
                  />
                </>
              )}

              {/* Active Zone Circle */}
              <Circle
                center={zoneCenter}
                radius={zoneRadius}
                pathOptions={{ 
                  color: '#3b82f6', 
                  fillColor: '#3b82f6', 
                  fillOpacity: 0.1,
                  dashArray: '5, 10'
                }}
              />

              {/* Draggable Zone Center Marker */}
              <Marker
                position={zoneCenter}
                draggable={true}
                eventHandlers={{
                  dragend: (e) => {
                    const marker = e.target;
                    const position = marker.getLatLng();
                    setZoneCenter([position.lat, position.lng]);
                  },
                }}
                icon={L.divIcon({
                  className: "zone-center-icon",
                  html: `
                    <div style="background-color: #3b82f6; width: 24px; height: 24px; border-radius: 50%; border: 4px solid white; box-shadow: 0 0 10px rgba(0,0,0,0.3); display: flex; align-items: center; justify-center; color: white;">
                       <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="3" fill="none" style="margin: auto"><path d="M12 5v14M5 12h14"/></svg>
                    </div>`,
                  iconSize: [24, 24],
                  iconAnchor: [12, 12]
                })}
              >
                <Popup>
                   <p className="text-xs font-bold">Zone Center (Drag to move)</p>
                </Popup>
              </Marker>

              {clusteredUsers.map((user) => {
                const isOutlier = user.routeIndex === -1;
                const markerColor = isOutlier ? OUTLIER_COLOR : routeColors[user.routeIndex % routeColors.length];
                const markerIcon = createColoredIcon(markerColor);
                
                return (
                <Marker 
                  key={user.id || user._id} 
                  position={user.coords}
                  icon={markerIcon}
                >
                  <Popup className="custom-popup">
                    <div className="p-1">
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <h3 className="font-bold text-sm truncate">{user.name}</h3>
                        <Badge 
                          style={{ backgroundColor: markerColor, color: 'white' }} 
                          className="text-[10px] px-1.5 py-0 whitespace-nowrap"
                        >
                          {isOutlier ? "Out of Zone" : `Route ${user.routeIndex + 1}`}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{user.location_string || user.address}</p>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={user.subscription === "Premium" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
                          {user.subscription}
                        </Badge>
                        <Badge variant={user.status === "active" ? "success" : "destructive"} className="text-[10px] px-1.5 py-0 capitalize">
                          {user.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span>{user.coords[0].toFixed(4)}, {user.coords[1].toFixed(4)}</span>
                      </div>
                    </div>
                  </Popup>
                </Marker>
                );
              })}
            </MapContainer>
            
            {/* Route Legend overlay */}
            <div className="absolute top-4 right-4 z-[400] bg-background/90 backdrop-blur p-3 rounded-lg border border-border shadow-md text-[10px] text-muted-foreground pointer-events-none">
              Tip: Drag the blue circle center to move the active zone.
            </div>

            {clusteredUsers.length > 0 && (
              <div className="absolute bottom-4 left-4 z-[400] bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-3 rounded-lg border border-border shadow-lg max-h-[40%] overflow-y-auto w-48">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                  <Route className="w-4 h-4" /> 
                  Zone Statistics
                </h4>
                <div className="space-y-2">
                  {Array.from({ length: Math.min(parseInt(numRoutes), clusteredUsers.filter(u => u.routeIndex !== -1).length) }).map((_, i) => {
                    const count = clusteredUsers.filter(u => u.routeIndex === i).length;
                    if (count === 0) return null;
                    return (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full shadow-sm" 
                            style={{ backgroundColor: routeColors[i % routeColors.length] }}
                          ></div>
                          <span>Route {i + 1}</span>
                        </div>
                        <span className="text-muted-foreground font-medium">{count}</span>
                      </div>
                    );
                  })}
                  
                  {/* Outliers */}
                  {clusteredUsers.filter(u => u.routeIndex === -1).length > 0 && (
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full shadow-sm" 
                          style={{ backgroundColor: OUTLIER_COLOR }}
                        ></div>
                        <span>Out of Zone</span>
                      </div>
                      <span className="text-muted-foreground font-medium">
                        {clusteredUsers.filter(u => u.routeIndex === -1).length}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {users.length === 0 && !error && (
        <div className="bg-muted/30 border border-dashed border-border rounded-lg p-8 text-center">
          <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-20" />
          <h3 className="text-lg font-medium text-foreground">No locations found</h3>
          <p className="text-muted-foreground max-w-md mx-auto mt-1">
            We couldn't find any users with valid coordinates. Add latitude and longitude to user profiles in the Users management tab to see them here.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-center">
          {error}
        </div>
      )}
    </div>
  );
};

export default UserMap;
